import ImpactActionCardCorner from "../../common/components/ActionCard/ActionCardCorner/ImpactActionCardCorner";
import { WideWidthContainer } from "../../common/components/layout/Container";
import Heading from "../../common/components/ui/Heading";
import Paragraph from "../../common/components/ui/Paragraph";
import Tag from "../../common/components/ui/Tag";
import { ExternalLinkButton } from "../../common/components/ui/buttons/ExternalLinkButton";
import { TextLinkButton } from "../../common/components/ui/buttons/TextLinkButton";
import NotificationDialog from "../../common/components/ui/dialogs/NotificationDialog";
import {
  ActionCategory,
  TagTheme,
} from "@pslifestyle/common/src/types/planTypes";
import React, { useState } from "react";

export interface ActionPreviewProps {
  category: ActionCategory;
  title: string;
  description: React.ReactNode[];
  help: React.ReactNode[];
  links?: { link: string; text: string }[];
}

export interface ActionTextProps {
  seeMoreText: string;
  categoryText: string;
  hideText: string;
  needHelpText: string;
  helpfulLinksText: string;
  closeText: string;
}

export const ActionPreview = ({
  category,
  title,
  description,
  help,
  links,
  seeMoreText,
  categoryText,
  hideText,
  needHelpText,
  helpfulLinksText,
  closeText,
}: ActionPreviewProps & ActionTextProps) => {
  const [showMore, setShowMore] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  return (
    <>
      <WideWidthContainer className="px-4 mb-4">
        <div className="bg-basic-white rounded-2xl">
          <div className="flex flex-row">
            <div className="grow pt-6 pl-5 pb-1">
              <Tag
                filter={category.toLowerCase()}
                key={category}
                variant={category.toLowerCase() as TagTheme}
              >
                {categoryText}
              </Tag>
              <Heading level={3} type="title-md" className="mt-3">
                {title}
              </Heading>
              <TextLinkButton
                onClick={() => setShowMore(!showMore)}
                icon={{
                  position: "right",
                  size: "medium",
                  type: showMore ? "ChevronUp" : "ChevronDown",
                }}
                buttonClassName="mt-2.5"
              >
                {showMore ? hideText : seeMoreText}
              </TextLinkButton>
            </div>
            <div>
              <ImpactActionCardCorner
                category={category}
                calculatedImpact={100}
                percentReduction={50}
              />
            </div>
          </div>
          <div
            className={`ml-3 mb-3 overflow-hidden transition-max-height ease-in-out duration-300 ${
              showMore ? "max-h-screen" : "max-h-0"
            }`}
          >
            <div className="flex flex-col gap-1 px-2 mb-2 body-md">
              {description}
              {help && (
                <TextLinkButton
                  className="text-cyan-dark items-start"
                  buttonClassName="flex"
                  onClick={() => setShowHelp(true)}
                >
                  {needHelpText}
                </TextLinkButton>
              )}
            </div>
          </div>
        </div>
      </WideWidthContainer>
      <NotificationDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        closeButtonText={closeText}
      >
        <Heading
          level={2}
          type="headline-sm-b"
          className="my-2 self-start text-left"
        >
          {title}
        </Heading>
        <div className="relative self-start text-left">{help}</div>
        {links && links.length > 0 && (
          <>
            <Paragraph type="body-lg-b" className="w-full text-left my-3">
              {helpfulLinksText}
            </Paragraph>
            <div className="flex flex-col w-full gap-2">
              {links.map((link) => (
                <ExternalLinkButton
                  theme="secondary"
                  linkOut
                  key={link.link}
                  url={link.link}
                  text={link.text ?? "N/A"}
                />
              ))}
            </div>
          </>
        )}
      </NotificationDialog>
    </>
  );
};
